import { Table, Text, Badge, Center, Title, Alert } from "@mantine/core";
// import { useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { supportedCoins, getEnabledCoins } from "../../config/coins.config";
import { iconMap } from "../../config/iconMap";
import { buildApiUrl } from "../../config/endpoints";
import { useNavigate } from "react-router-dom";
// import { ADD_LOG } from "../../queries/user-analytics"; // added for tracking user data

import "./TradesPage.css";

const TradesPage = () => {
  const navigate = useNavigate();
  
  // Initialize symbolData from the coins config
  const initialSymbolData = getEnabledCoins()
    .filter(coin => iconMap[coin.symbol])
    .map(coin => ({
      ticker: coin.symbol,
      name: coin.name,
      price: 0.0,
      change: 0.0,
      image: iconMap[coin.symbol],
      code: `${coin.baseCurrency}-${coin.quoteCurrency}`,
      altCode: coin.code
    }));
  
  const [symbolData, setSymbolData] = useState(initialSymbolData);
  const symbolDataRef = useRef(initialSymbolData);

  // added for tracking user data
    // const [logEventMutation] = useMutation(ADD_LOG, {
    //     onCompleted: ({ addLog }) => {
    //         // Access the HTTP response directly
    //         const { status, message } = addLog;
    //         if (status === "success") {
    //             console.log("Event logged successfully:", message);
    //         } else {
    //             console.error("Failed to log event:", message);
    //         }
    //     },
    //     onError: (error) => {
    //         console.error("Error logging event:", error.message);
    //     }
    // });

    // const logEvent = (eventType, additionalData, _userid) => {
    //     const eventData = {
    //         userid: Number(_userid),
    //         event_type: eventType,
    //         timestamp: new Date().toISOString(),
    //         additional_data: JSON.stringify(additionalData)

    //     };

    //     logEventMutation({ variables: eventData });
    // };

  const fetchLatestPrice = async(symbol) => {
    try {
      const response = await fetch(buildApiUrl('/latestprice') + '?' + new URLSearchParams({ symbol: symbol.code }));
      if (!response.ok) {
        throw new Error(`Network response was not ok for symbol: ${symbol.code}`);
      }
      const result = await response.json();
      
      const newPrice = parseFloat(result.latest_price);
      const priceChange = symbol.price === 0 ? 0 : ((newPrice - symbol.price) / symbol.price) * 100;

      const newSymbolData = {
        ...symbol,
        price: newPrice,
        change: priceChange,
        unavailable: false
      };
      return newSymbolData
    } catch (err) {
      console.error('Error fetching or processing data for symbol:', symbol.code, err.message);
      // Return the symbol with unchanged price but mark as unavailable
      return {
        ...symbol,
        price: 0.0,
        change: 0.0,
        unavailable: true
      };
    }
  }

  useEffect(() => {
    const fetchSymbolData = async () => {
      try {
        const updatedSymbolData = await Promise.all(
          symbolDataRef.current.map((symbol) => fetchLatestPrice(symbol))
        );

        symbolDataRef.current = updatedSymbolData;
        setSymbolData(updatedSymbolData);
        // console.log(updatedSymbolData);
      } catch (err) {
        console.error('Error fetching symbol data:', err.message);
      }
    };

    fetchSymbolData();
    // logEvent('TradeSymbolPage', data.currentUser, data.currentUser.userid); // added for tracking user data
    const intervalId = setInterval(fetchSymbolData, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Get precision from the configuration for proper price formatting
  const getPrecision = (ticker) => {
    const coin = supportedCoins.find(c => c.symbol === ticker);
    return coin ? coin.priceDecimals : 2;
  };

  return (
    <div>
      <div className="watchlist-title">
        <Title order={2}>Watchlist</Title>
      </div>
      <Alert color="blue" variant="light" mb="md">
        All order times are shown in Singapore Time (SGT, UTC+8).
      </Alert>
      {/* Removed striped table effect to allow hover effect --> { <Table striped></Table> } */}
      <Table className="watchlist-table">
        <thead>
          <tr>
            <th>
              <Text fz="lg">Symbol</Text>
            </th>
            <th>
              <Center maw={400} mx="auto">
                <Text fz="lg">Price (USD)</Text>
              </Center>
            </th>
            <th>
              <Center maw={400} mx="auto">
                <Text fz="lg">Change (%)</Text>
              </Center>
            </th>
          </tr>
        </thead>
        <tbody>
          {symbolData.map((symbol) => (
            <tr
              key={symbol.name}
              onClick={() => symbol.unavailable ? null : navigate("/trade/" + symbol.altCode)}
              className={`symbol-row ${symbol.unavailable ? 'unavailable-symbol' : ''}`}
              style={symbol.unavailable ? { cursor: 'default', opacity: 0.7 } : {}}
            >
              <td className="flex-horizontal">
                <img
                  src={symbol.image}
                  alt={symbol.name}
                  className="symbol-icon"
                />
                <div className="flex-vertical">
                  <Text fz="xl" fw={700}>
                    {symbol.ticker}
                  </Text>
                  <Text fz="xs" c="dimmed">
                    {symbol.name}
                  </Text>
                </div>
              </td>
              <td>
                <Center maw={400} mx="auto">
                  {symbol.unavailable ? (
                    <Text fz="md" c="dimmed">Not available</Text>
                  ) : (
                    <Text fz="lg" fw={700}>
                      {symbol.price.toFixed(getPrecision(symbol.ticker))}
                    </Text>
                  )}
                </Center>
              </td>
              <td>
                <Center maw={400} mx="auto">
                  {symbol.unavailable ? (
                    <Badge color="gray" variant="outline">
                      <Text fz="lg">N/A</Text>
                    </Badge>
                  ) : (
                    <Badge
                      color={symbol.change >= 0 ? "green" : "red"}
                      variant="outline"
                      className="symbol-change-badge"
                    >
                      <Text fz="lg">{symbol.change.toFixed(2)}%</Text>
                    </Badge>
                  )}
                </Center>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default TradesPage;